// Measure-based folding for the desktop nav row.
//
// The Navbar renders every nav item it can and this hook decides, from real
// measurements, how many leading items actually fit; the rest fold into an
// overflow menu. A ResizeObserver on the nav container drives recomputation,
// and each rendered item reports its intrinsic width through a ref callback.
// Widths are cached by path, so items keep a known width while folded.
//
// The fitting math lives in pure, exported functions (computeVisibleCount,
// splitByOverflow) so it can be unit-tested with synthetic widths — jsdom
// reports zero for every layout box, which conveniently means tests without a
// measurement mock keep every item visible and never see the overflow trigger.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// An item only unfolds once there is this much clearance beyond its own width,
// so a container sitting exactly on a fold boundary cannot oscillate.
export const OVERFLOW_HYSTERESIS_PX = 24;

// Space reserved for the overflow trigger (a large IconButton) plus the flex
// gap before it, counted only when at least one item folds.
export const OVERFLOW_TRIGGER_RESERVE_PX = 64;

export interface ComputeVisibleCountOptions {
  // Committed count from the previous layout pass; unfolding beyond it
  // requires OVERFLOW_HYSTERESIS_PX of clearance. Defaults to "everything".
  previousVisibleCount?: number;
  triggerReserve?: number;
  hysteresis?: number;
}

// Pure fit computation: how many leading items fit in `containerWidth` after
// subtracting `reserved` (space that is always consumed, e.g. a sync icon).
// Item widths must already include any per-item gap. When the active route's
// item (activeIndex) would fold, the last visible slot is costed at the active
// item's width instead, matching the promotion splitByOverflow performs.
export const computeVisibleCount = (
  itemWidths: number[],
  containerWidth: number,
  reserved: number,
  activeIndex: number,
  options: ComputeVisibleCountOptions = {}
): number => {
  const total = itemWidths.length;
  if (total === 0) return 0;
  const {
    previousVisibleCount = total,
    triggerReserve = OVERFLOW_TRIGGER_RESERVE_PX,
    hysteresis = OVERFLOW_HYSTERESIS_PX,
  } = options;
  const available = containerWidth - reserved;
  const prev = Math.max(0, Math.min(previousVisibleCount, total));

  const consumed = (count: number): number => {
    const promoted = count > 0 && activeIndex >= count && activeIndex < total;
    let width = 0;
    for (let i = 0; i < count; i += 1) {
      width += promoted && i === count - 1 ? itemWidths[activeIndex] : itemWidths[i];
    }
    if (count < total) width += triggerReserve;
    return width;
  };

  // Largest count that fits at all. Folding applies immediately — an
  // overflowing item must never stay inline.
  let fitCount = 0;
  for (let count = total; count >= 0; count -= 1) {
    if (consumed(count) <= available) {
      fitCount = count;
      break;
    }
  }
  if (fitCount <= prev) return fitCount;

  // Unfolding: each item comes back only with hysteresis clearance beyond its
  // own width, so the width that just caused a fold cannot immediately unfold.
  let count = prev;
  while (count < total && consumed(count + 1) + hysteresis <= available) {
    count += 1;
  }
  return count;
};

// Split items into the inline set and the folded set. If the active route's
// item would fold, it is promoted into the last visible slot and the item it
// displaces folds instead.
export const splitByOverflow = <T,>(
  items: T[],
  visibleCount: number,
  activeIndex: number
): { visible: T[]; overflow: T[] } => {
  const count = Math.max(0, Math.min(visibleCount, items.length));
  if (count >= items.length) return { visible: items, overflow: [] };
  const visibleIndexes = items.slice(0, count).map((_, index) => index);
  if (count > 0 && activeIndex >= count && activeIndex < items.length) {
    visibleIndexes[count - 1] = activeIndex;
  }
  const visibleSet = new Set(visibleIndexes);
  return {
    visible: visibleIndexes.map((index) => items[index]),
    overflow: items.filter((_, index) => !visibleSet.has(index)),
  };
};

export interface NavOverflowItem {
  path: string;
}

export interface UseNavOverflowResult<T> {
  // Attach to the flex container that holds the inline items + trigger.
  containerRef: (node: HTMLElement | null) => void;
  // setItemRef(path) yields a ref callback for that item's rendered element.
  setItemRef: (path: string) => (node: HTMLElement | null) => void;
  visible: T[];
  overflow: T[];
}

export const useNavOverflow = <T extends NavOverflowItem>(
  items: T[],
  activeIndex: number,
  options: { itemGap?: number; reserved?: number } = {}
): UseNavOverflowResult<T> => {
  const { itemGap = 16, reserved = 0 } = options;
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const widthCache = useRef(new Map<string, number>());
  const itemEls = useRef(new Map<string, HTMLElement>());
  const observerRef = useRef<ResizeObserver | null>(null);

  const containerRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    const update = () => setContainerWidth(node.offsetWidth);
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(node);
      observerRef.current = observer;
    }
    update();
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const setItemRef = useCallback(
    (path: string) => (node: HTMLElement | null) => {
      if (node) itemEls.current.set(path, node);
      else itemEls.current.delete(path);
    },
    []
  );

  // Fold the rendered items' widths into the cache (folded items keep their
  // last measured width) and recompute the fit. visibleCount is a dependency
  // on purpose: a count change re-renders a different item set, and the
  // re-run measures the newly rendered items; the computation is convergent
  // (fold is monotonic, unfold needs hysteresis clearance), so the
  // conditional setState settles instead of looping.
  useLayoutEffect(() => {
    itemEls.current.forEach((el, path) => {
      const width = el.offsetWidth;
      if (width > 0) widthCache.current.set(path, width + itemGap);
    });
    // Until the container and every item have real measurements (first paint,
    // a newly shown item, or jsdom), everything stays visible.
    let next = items.length;
    if (containerWidth !== null && containerWidth > 0) {
      const widths = items.map((item) => widthCache.current.get(item.path));
      if (widths.every((width): width is number => width !== undefined)) {
        next = computeVisibleCount(widths, containerWidth, reserved, activeIndex, {
          previousVisibleCount: Math.min(visibleCount, items.length),
        });
      }
    }
    if (next !== visibleCount) setVisibleCount(next);
  }, [items, containerWidth, itemGap, reserved, activeIndex, visibleCount]);

  return useMemo(() => {
    const { visible, overflow } = splitByOverflow(
      items,
      Math.min(visibleCount, items.length),
      activeIndex
    );
    return { containerRef, setItemRef, visible, overflow };
  }, [items, visibleCount, activeIndex, containerRef, setItemRef]);
};

export default useNavOverflow;
